import axios from 'axios'
import router from '@/router'
import { Message } from 'element-ui'
import { getAdminToken, clearAdminAuth } from '@/utils/auth'

const API_BASE_URL = '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Request interceptor to add token
api.interceptors.request.use(
  config => {
    const token = getAdminToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      config.headers.token = token
    }
    // 如果是 FormData，删除 Content-Type 让浏览器自动设置（包括 boundary）
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 统一处理未授权（401）
function handleUnauthorized(message) {
  // 清除本地登录状态
  clearAdminAuth()
  // 避免在登录页重复跳转
  if (router.currentRoute.path !== '/login') {
    Message.error(message || '登录已过期，请重新登录')
    router.push('/login')
  }
}

// Response interceptor to handle backend response format
api.interceptors.response.use(
  response => {
    const { data } = response
    // 部分后端可能使用业务 code 标识未授权
    if (data && (data.code === 401 || data.status === 401)) {
      handleUnauthorized(data.message || data.msg || '登录已过期，请重新登录')
      // 返回一个被拒绝的 Promise，阻止后续 then 继续执行
      return Promise.reject(new Error('未授权或登录已过期'))
    }

    if (data.code === 200) {
      return data.data
    } else {
      throw new Error(data.message || '请求失败')
    }
  },
  error => {
    // 如果是 HTTP 401（未授权）
    if (error.response && error.response.status === 401) {
      const msg =
        (error.response.data && (error.response.data.message || error.response.data.msg)) ||
        '登录已过期，请重新登录'
      handleUnauthorized(msg)
      return Promise.reject(new Error('未授权或登录已过期'))
    }

    throw new Error(error.message || '网络错误')
  }
)

// Category API
export const categoryApi = {
  // Get all categories with optional level and parent_id filters
  getAll: (params = {}) => api.get('/category', { params }),
  
  // Get category by id
  getById: (id) => api.get(`/category/${id}`),
  
  // Create new category
  create: (data) => api.post('/category', data),
  
  // Update category
  update: (id, data) => api.put(`/category/${id}`, data),
  
  // Delete category
  delete: (id) => api.delete(`/category/${id}`),
  
  // Get categories by level and parent_id
  getByLevelAndParent: (level, parentId) => 
    api.get('/category', { params: { level, parent_id: parentId } })
}

// Resource API
export const resourceApi = {
  // Get all resources with optional category_id filter
  getAll: (params = {}) => api.get('/resources', { params }),
  
  // Get resource by id
  getById: (id) => api.get(`/resources/${id}`),
  
  // Create new resource(s) - 后端现在接收 List<CategoryResource>，这里统一按数组提交
  create: (data) => {
    const payload = Array.isArray(data) ? data : [data]
    return api.post('/resources', payload)
  },
  
  // Update resource
  update: (id, data) => api.put(`/resources/${id}`, data),
  
  // Delete resource
  delete: (id) => api.delete(`/resources/${id}`),
  
  // Get resources by category_id
  getByCategory: (categoryId) => 
    api.get('/resources', { params: { category_id: categoryId } })
}

// Login API
export const loginApi = {
  // Login
  login: (data) => api.post('/login', data),
  
  // Logout
  logout: () => api.post('/logout')
}

// File upload API（单独使用原生 axios，避免被统一响应拦截器把非 200 code 当作失败）
export const uploadApi = {
  // Upload single file
  uploadFile: (file, onProgress) => {
    const formData = new FormData()
    formData.append('file', file)

    const token = getAdminToken()

    return axios.post(`${API_BASE_URL}/upload`, formData, {
      timeout: 0, // 无超时限制，支持大文件上传
      headers: {
        // 不设置 Content-Type，让浏览器自动设置（包含 boundary）
        ...(token
          ? {
              Authorization: `Bearer ${token}`,
              token
            }
          : {})
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          onProgress(progress)
        }
      }
    }).then((response) => {
      // 这里不依赖后端的业务 code，直接把 data 返回给调用方自行解析
      return response.data
    })
  }
}

// Home Image API
export const homeImageApi = {
  // Get all home images
  getAll: () => api.get('/home-image'),
  
  // Get home image by id
  getById: (id) => api.get(`/home-image/${id}`),
  
  // Create new home image
  create: (data) => api.post('/home-image', data),
  
  // Update home image
  update: (id, data) => api.put(`/home-image/${id}`, data),
  
  // Delete home image
  delete: (id) => api.delete(`/home-image/${id}`)
}

// Admin API
export const adminApi = {
  // Admin login
  login: (data) => api.post('/admin/login', data),
  
  // Change admin password
  changePassword: (data) => api.put('/admin/password', data),
  
  // Admin logout
  logout: () => api.post('/admin/logout')
}

// Config API
export const configApi = {
  // Get all configs
  getAll: () => api.get('/config'),
  
  // Get config by id
  getById: (id) => api.get(`/config/${id}`),
  
  // Get config by key
  getByKey: (key) => api.get(`/config/key/${key}`),
  
  // Create new config
  create: (data) => api.post('/config', data),
  
  // Update config
  update: (id, data) => api.put(`/config/${id}`, data),
  
  // Delete config
  delete: (id) => api.delete(`/config/${id}`)
}

export default api
